import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Minus, Plus, Printer, Warehouse } from "lucide-react";

// ALL PALLET RACKING RUNS THE SAME (his rule 2026-10-20). He only ever says HOW MANY — the server works out
// every code on the warehouse's own pattern and shows them before anything is made.
const Stepper = ({ id, label, value, set, max = 60 }) => (
  <div className="flex items-center justify-between gap-3">
    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
    <div className="flex items-center gap-2">
      <Button type="button" data-testid={`${id}-minus`} variant="outline" className="rounded-lg h-11 w-11 p-0 border-slate-200"
        onClick={() => set(Math.max(1, value - 1))}><Minus className="w-4 h-4" /></Button>
      <span data-testid={`${id}-value`} className="font-display text-2xl font-bold tabular-nums w-10 text-center">{value}</span>
      <Button type="button" data-testid={`${id}-plus`} variant="outline" className="rounded-lg h-11 w-11 p-0 border-slate-200"
        onClick={() => set(Math.min(max, value + 1))}><Plus className="w-4 h-4" /></Button>
    </div>
  </div>
);

const TITLES = { new: "Add pallet racking", level: "Add a level", bays: "Add bays", floor: "Add a floor position" };

const PlanView = ({ plan }) => (
  <div data-testid="racking-plan" className="space-y-2">
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-sm"><b data-testid="racking-plan-count">{plan.count}</b> labels on <b className="font-mono">{plan.rack_code}</b>{plan.new_rack ? ` (${plan.rack_name}, new)` : ""}</div>
      <div className="text-[12px] font-mono text-slate-600 mt-0.5" data-testid="racking-plan-range">{plan.first} … {plan.last}</div>
    </div>
    {plan.new_rack && (
      <div className="text-[12px] text-slate-600" data-testid="racking-plan-levels">
        Levels, bottom up: {plan.levels.map((l) => `${l.level} = ${l.name}`).join(" · ")}
      </div>
    )}
    <div className="max-h-36 overflow-y-auto flex flex-wrap gap-1.5" data-testid="racking-plan-codes">
      {plan.codes.map((c) => <span key={c} className="font-mono text-[11px] rounded-md bg-white border border-slate-200 px-1.5 py-0.5">{c}</span>)}
    </div>
    <p className="text-[11px] text-slate-500" data-testid="racking-plan-rule">{plan.rule}</p>
  </div>
);

export const RackingDialog = ({ target, zones = [], onClose, onDone, onPrintRack }) => {
  const mode = target?.mode;
  const [zoneId, setZoneId] = useState(target?.zone?.id || "");
  const [bays, setBays] = useState(8);
  const [levels, setLevels] = useState(6);
  const [floor, setFloor] = useState(true);
  const [count, setCount] = useState(1);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const body = mode === "new" ? { zone_id: zoneId, bays, levels, floor }
    : { rack_id: target?.rack?.id, add_levels: mode === "level" ? count : 0, add_bays: mode === "bays" ? count : 0,
        add_floor: mode === "floor" };
  const key = JSON.stringify(body);

  useEffect(() => {
    if (!mode || (mode === "new" && !zoneId)) { setPlan(null); return undefined; }
    const t = setTimeout(async () => {
      try { setPlan((await api.post("/locations/racking/plan", JSON.parse(key))).data); }
      catch (e) { setPlan(null); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    }, 250);
    return () => clearTimeout(t);
  }, [key, mode, zoneId]);

  const build = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/locations/racking/build", body);
      setResult(data);
      toast.success(`${data.rack_code}: ${data.made} position${data.made === 1 ? "" : "s"} made`);
      onDone?.(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const place = mode === "new" ? (zones.find((z) => z.id === zoneId) || target?.zone) : target?.rack;
  return (
    <Dialog open={!!target} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="racking-dialog" className="rounded-xl border-slate-200 sm:max-w-lg max-h-kb overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Warehouse className="w-5 h-5 text-[#B01E28]" />{TITLES[mode]}{place ? ` — ${place.name}` : ""}</DialogTitle>
          <DialogDescription>You only choose how many. The app makes every code on the warehouse's own pattern.</DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-4">
            {mode === "new" && !target?.zone && (
              <Select value={zoneId} onValueChange={setZoneId}>
                <SelectTrigger data-testid="racking-zone-select" className="rounded-lg h-11 border-slate-200"><SelectValue placeholder="Which zone does it stand in?" /></SelectTrigger>
                <SelectContent>{zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name} · {z.short_code}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {mode === "new" ? (
              <>
                <Stepper id="racking-bays" label="Bays (side by side)" value={bays} set={setBays} />
                <Stepper id="racking-levels" label="Levels (bottom to top)" value={levels} set={setLevels} max={24} />
                <label className="flex items-start gap-3 rounded-lg border border-slate-200 px-3 py-2.5 cursor-pointer">
                  <Switch data-testid="racking-floor-toggle" checked={floor} onCheckedChange={setFloor} className="mt-0.5" />
                  <span className="text-sm text-slate-700"><b>The bottom level is the floor</b> — pallets also sit on the ground under the first beam, like every bay inside (level 0, "Floor position"). Not sure yet? Leave it off — <b>Add a floor position</b> adds it later without changing any label.</span>
                </label>
              </>
            ) : mode === "floor" ? (
              <p className="text-sm text-slate-700" data-testid="racking-floor-note">A floor position <b>0</b> under every bay that doesn't have one — for pallets on the ground under the first beam. Nothing already on the rack changes.</p>
            ) : (
              <Stepper id="racking-count" label={mode === "level" ? "Levels to add on top" : "Bays to add on the end"} value={count} set={setCount} max={mode === "level" ? 10 : 60} />
            )}
            {plan ? <PlanView plan={plan} /> : <div className="text-sm text-slate-400">{mode === "new" && !zoneId ? "Pick the zone first." : "Working out the codes…"}</div>}
          </div>
        ) : (
          <div data-testid="racking-result" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            <b className="font-mono">{result.rack_code}</b> — {result.made} made, <span className="font-mono">{result.first}</span> to <span className="font-mono">{result.last}</span>. Print the shelf and scan labels now, or later from the rack's menu → Print rack labels.
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" data-testid="racking-close" className="rounded-lg h-11 border-slate-200" onClick={onClose}>{result ? "Done" : "Cancel"}</Button>
          {!result ? (
            <Button data-testid="racking-build" disabled={busy || !plan} onClick={build} className="rounded-lg h-11 bg-[#E0781A] hover:bg-[#C76714] text-white font-bold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Make ${plan?.count || ""} positions`}
            </Button>
          ) : (
            <Button data-testid="racking-print" onClick={() => onPrintRack({ id: result.rack_id, short_code: result.rack_code, name: result.rack_name })}
              className="rounded-lg h-11 bg-[#1F1F1F] hover:bg-black text-white font-bold">
              <Printer className="w-4 h-4 mr-1.5" />Print rack labels
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
